'use client';
import type { ReactElement } from 'react';
import { Fragment } from 'react';
import { Controller } from 'react-hook-form';

import { FormField } from '../shared/FormField';
import { ValuePairFields } from '../shared/ValuePairFields';
import { PropertyInstructionSection } from '../shared/PropertyInstructionSection';
import { ItemInfoAddressSearch } from '../shared/ItemInfoAddressSearch';
import type { InventoryFormTypeProps } from '../types';
import { getFormItem, getFormItemInfo } from '../utils';
import type { PropertyInstruction } from '@/lib/services/redemption';
import { useFieldChanges } from '@/components/documents/ai-import/review-card/field-changes-context';
import { useLawFirmProfile } from '@/lib/services/account';
import {
  getTitleReferenceLabel,
  getTitleReferencePlaceholder,
  resolveTitleReferenceState
} from '@/lib/constants/title-reference';

/** Exported so the attribute-drawer recipe (`inventory-attribute-specs.tsx`,
 * [INVDRAW-9]) binds to this list rather than restating it. */
export const ESTATE_TYPES = [
  { value: 'fee-simple', label: 'Fee Simple (Freehold)' },
  { value: 'leasehold', label: 'Leasehold' },
  { value: 'crown', label: 'Crown Land' },
  { value: 'life-estate', label: 'Life Estate' },
  { value: 'easement-only', label: 'Easement Only' },
  { value: 'other', label: 'Other' }
];

/**
 * Real Estate inventory form component
 * Handles both read-only and edit modes for real estate items
 *
 * Fields:
 * - Address (shared address-search: structured street/suburb/state/postcode/
 *   country with autocomplete + manual-entry fallback)
 * - Title reference (label/format per state/territory — LF-4071)
 * - Estate type (Fee Simple / Leasehold / Crown / Life Estate / Easement Only / Other)
 * - Date of Death Value
 * - Current Value
 * - Notes
 */
export function RealEstateForm({
  form,
  mode,
  isInstitutionEditable = true,
  hideEstateReferenceField,
  contacts
}: InventoryFormTypeProps): ReactElement {
  const item = getFormItem(form);
  const itemInfo = getFormItemInfo(form);

  // Field-changes context (populated when the card is rendered inside an
  // AI update review wrapper). Lets us light up the "NEW" enrichment pill
  // on fields the AI is supplying for the first time.
  const fieldChanges = useFieldChanges();
  const enrichment = (key: string) =>
    fieldChanges?.[key]?.changeType === 'enrichment'
      ? ('enrichment' as const)
      : undefined;

  // Title-reference terminology and format hint follow the property's state,
  // falling back to the law firm's state when the property has none (LF-4071).
  const { profile } = useLawFirmProfile();
  const titleState = resolveTitleReferenceState(
    itemInfo.state as string | null | undefined,
    profile?.businessAddress?.state
  );
  const titleLabel = getTitleReferenceLabel(titleState);
  const titlePlaceholder = getTitleReferencePlaceholder(titleState);

  if (mode === 'read') {
    // Address is already shown in card header title, so don't duplicate it
    return (
      <Fragment>
        {/* Estate Reference Number - only show when NOT in draft status */}
        {!isInstitutionEditable && !hideEstateReferenceField && (
          <FormField
            label="Estate Reference Number"
            value={(item.estateReference as string) || '—'}
            mode="read"
          />
        )}

        {/* Title reference (label/format per jurisdiction — LF-4071) */}
        {Boolean(itemInfo.folioIdentifier) && (
          <FormField
            label={titleLabel}
            value={itemInfo.folioIdentifier as string}
            mode="read"
            changeType={enrichment('itemInfo.folioIdentifier')}
          />
        )}

        {/* Estate type */}
        {Boolean(itemInfo.estateType) && (
          <FormField
            label="Estate type"
            value={
              ESTATE_TYPES.find((o) => o.value === itemInfo.estateType)
                ?.label ?? (itemInfo.estateType as string)
            }
            mode="read"
            changeType={enrichment('itemInfo.estateType')}
          />
        )}

        {/* Values */}
        <ValuePairFields
          mode="read"
          leftLabel="Date of Death Value"
          leftValue={itemInfo.valueAtDateOfDeath as number}
          rightLabel="Current Value"
          rightValue={itemInfo.currentValue as number}
          leftChangeType={enrichment('itemInfo.valueAtDateOfDeath')}
          rightChangeType={enrichment('itemInfo.currentValue')}
        />

        {/* Notes */}
        {Boolean(itemInfo.notes) && (
          <FormField
            label="Notes"
            value={itemInfo.notes as string}
            mode="read"
            multiline
          />
        )}

        {/* Property instruction summary */}
        <PropertyInstructionSection
          mode="read"
          ownershipType={itemInfo.ownershipType as string | null | undefined}
          instruction={
            itemInfo.instruction as PropertyInstruction | null | undefined
          }
          transferSubFlow={
            itemInfo.transferSubFlow as
              | import('@/lib/services/redemption').PropertyTransferSubFlow
              | null
              | undefined
          }
          hasContraryIntention={!!itemInfo.hasContraryIntention}
          percentageOwned={
            itemInfo.percentageOwned as number | null | undefined
          }
          beneficiaryHoldingType={
            itemInfo.beneficiaryHoldingType as
              | import('@/lib/services/redemption').BeneficiaryHoldingType
              | null
              | undefined
          }
          milestoneSummary={
            (itemInfo.milestoneSummary as { label?: string } | undefined)?.label
          }
          transferBeneficiaries={
            itemInfo.transferBeneficiaries as
              | Array<{
                  contactId: number;
                  contact?: { id: number; firstName: string; lastName: string };
                  percentage?: number;
                }>
              | undefined
          }
        />
      </Fragment>
    );
  }

  // Edit mode - wrapped with RHF Controllers
  return (
    <Fragment>
      {/* Address (street/suburb/state/postcode/country via shared search) */}
      <ItemInfoAddressSearch form={form} label="Property address" />

      {/* Estate Reference Number - only show when NOT in draft status */}
      {!isInstitutionEditable && !hideEstateReferenceField && (
        <Controller
          control={form.control}
          name="estateReference"
          render={({ field, fieldState }) => (
            <FormField
              label="Estate Reference Number"
              value={(field.value as string) || ''}
              mode="edit"
              placeholder="Enter estate reference number"
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
      )}

      {/* Title reference (label/placeholder/validation per jurisdiction — LF-4071) */}
      <Controller
        control={form.control}
        name="itemInfo.folioIdentifier"
        render={({ field, fieldState }) => (
          <FormField
            label={titleLabel}
            value={(field.value as string) || ''}
            mode="edit"
            placeholder={titlePlaceholder}
            onChange={field.onChange}
            error={fieldState.error?.message}
          />
        )}
      />

      {/* Estate type */}
      <Controller
        control={form.control}
        name="itemInfo.estateType"
        render={({ field, fieldState }) => (
          <FormField
            label="Estate type"
            value={(field.value as string) || ''}
            mode="edit"
            type="select"
            options={ESTATE_TYPES}
            placeholder="Select estate type"
            onChange={field.onChange}
            error={fieldState.error?.message}
          />
        )}
      />

      {/* Values */}
      <Controller
        control={form.control}
        name="itemInfo.valueAtDateOfDeath"
        render={({ field: leftField, fieldState: leftState }) => (
          <Controller
            control={form.control}
            name="itemInfo.currentValue"
            render={({ field: rightField, fieldState: rightState }) => (
              <ValuePairFields
                mode="edit"
                leftLabel="Date of Death Value"
                leftValue={leftField.value as number}
                rightLabel="Current Value"
                rightValue={rightField.value as number}
                onLeftChange={leftField.onChange}
                onRightChange={rightField.onChange}
                leftPlaceholder="0.00"
                rightPlaceholder="0.00"
                leftError={leftState.error?.message}
                rightError={rightState.error?.message}
              />
            )}
          />
        )}
      />

      <Controller
        control={form.control}
        name="itemInfo.notes"
        render={({ field, fieldState }) => (
          <FormField
            label="Notes"
            value={(field.value as string) || ''}
            mode="edit"
            type="textarea"
            placeholder="Add notes"
            onChange={field.onChange}
            error={fieldState.error?.message}
          />
        )}
      />

      {/* Property instruction fields */}
      <Controller
        control={form.control}
        name="itemInfo.ownershipType"
        render={({ field: ownershipField }) => (
          <Controller
            control={form.control}
            name="itemInfo.instruction"
            render={({ field: instructionField }) => (
              <Controller
                control={form.control}
                name="isNonEstate"
                render={({ field: nonEstateField }) => (
                  <Controller
                    control={form.control}
                    name="itemInfo.transferSubFlow"
                    render={({ field: subFlowField }) => (
                      <Controller
                        control={form.control}
                        name="itemInfo.transferBeneficiaries"
                        render={({ field: beneficiariesField }) => (
                          <Controller
                            control={form.control}
                            name="itemInfo.beneficiaryHoldingType"
                            render={({ field: holdingTypeField }) => (
                              <Controller
                                control={form.control}
                                name="itemInfo.hasContraryIntention"
                                render={({ field: contraryField }) => (
                                  <Controller
                                    control={form.control}
                                    name="itemInfo.percentageOwned"
                                    render={({ field: pctField }) => (
                                      <PropertyInstructionSection
                                        mode="edit"
                                        ownershipType={
                                          ownershipField.value as string | null
                                        }
                                        instruction={
                                          instructionField.value as PropertyInstruction | null
                                        }
                                        transferSubFlow={
                                          subFlowField.value as
                                            | string
                                            | null as
                                            | import('@/lib/services/redemption').PropertyTransferSubFlow
                                            | null
                                        }
                                        transferBeneficiaries={
                                          beneficiariesField.value as
                                            | Array<{
                                                contactId: number;
                                                contact?: {
                                                  id: number;
                                                  firstName: string;
                                                  lastName: string;
                                                };
                                              }>
                                            | undefined
                                        }
                                        beneficiaryHoldingType={
                                          holdingTypeField.value as
                                            | import('@/lib/services/redemption').BeneficiaryHoldingType
                                            | null
                                        }
                                        hasContraryIntention={
                                          !!contraryField.value
                                        }
                                        propertyState={
                                          itemInfo.state as string | null
                                        }
                                        percentageOwned={
                                          pctField.value as number | null
                                        }
                                        hasLinkedMortgage={
                                          !!item.linkedInventoryItemId
                                        }
                                        contacts={contacts}
                                        onOwnershipTypeChange={(
                                          type,
                                          isNonEstate
                                        ) => {
                                          ownershipField.onChange(type);
                                          nonEstateField.onChange(isNonEstate);
                                        }}
                                        onInstructionChange={(inst) => {
                                          instructionField.onChange(inst);
                                          if (
                                            inst !== 'transfer-to-beneficiary'
                                          ) {
                                            beneficiariesField.onChange([]);
                                            subFlowField.onChange(null);
                                            holdingTypeField.onChange(null);
                                            contraryField.onChange(false);
                                          }
                                        }}
                                        onTransferSubFlowChange={(sf) =>
                                          subFlowField.onChange(sf)
                                        }
                                        onTransferBeneficiariesChange={(bens) =>
                                          beneficiariesField.onChange(bens)
                                        }
                                        onBeneficiaryHoldingTypeChange={(ht) =>
                                          holdingTypeField.onChange(ht)
                                        }
                                        onContraryIntentionChange={(val) =>
                                          contraryField.onChange(val)
                                        }
                                        onPercentageOwnedChange={(val) =>
                                          pctField.onChange(val)
                                        }
                                      />
                                    )}
                                  />
                                )}
                              />
                            )}
                          />
                        )}
                      />
                    )}
                  />
                )}
              />
            )}
          />
        )}
      />
    </Fragment>
  );
}
